import { useEffect, useMemo, useRef, useState } from 'react';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { storage } from '../lib/awsStorage';
import { ensureContact } from '../lib/contacts';
import ContactAutocomplete from '../lib/ContactAutocomplete';
import { EXPENSE_CATEGORIES } from '../lib/expenseCategories';
import { formatMoney, formatBytes, formatDate } from '../lib/money';
import { fileToBase64 } from '../lib/fileEncoding';
import { getOutstanding, getDisplayStatus } from '../lib/invoiceCalc';
import FilePreviewModal from '../po/FilePreviewModal';
import '../po/drawer.css';

const MONO = '\'JetBrains Mono\', ui-monospace, \'SF Mono\', Menlo, monospace';

function DrawerStat({ label, value, sub, accent }) {
  return (
    <div>
      <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', color: accent ? '#B85C3C' : '#0F1419' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#5C6166', marginTop: 2, fontFamily: MONO }}>{sub}</div>}
    </div>
  );
}

const ACCEPTED_MIME = ['application/pdf', 'image/png', 'image/jpeg', 'application/rtf', 'text/rtf'];
// Some browsers report file.type as '' for RTF. Fall back to extension
// so picking/dropping an .rtf still works.
function isAcceptedInvoiceFile(file) {
  if (!file) return false;
  if (ACCEPTED_MIME.includes(file.type)) return true;
  return /\.(rtf|pdf|png|jpe?g)$/i.test(file.name || '');
}
const BUCKET = 'po-attachments';

function logGiActivity(invoiceId, session, payload) {
  if (!invoiceId || !session) return Promise.resolve();
  return aws.from('invoice_activity_log').insert([{
    invoice_id: invoiceId, actor_code: session.code, actor_name: session.name,
    action_type: payload.action_type, before_value: payload.before_value ?? null,
    after_value: payload.after_value ?? null, notes: payload.notes ?? null
  }]).then(({ error }) => { if (error) console.warn('[gi-activity]', error.message); });
}

function formatWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
}

export default function GeneralInvoiceDrawer({ session, invoice, extractFile, paidAmount = 0, catalog = [], onClose, onSaved }) {
  const isEdit = !!invoice?.id;
  const [tab, setTab] = useState(isEdit ? 'activity' : 'invoice');
  const [form, setForm] = useState(() => invoice ? {
    supplier_name: invoice.supplier_name || '', invoice_number: invoice.invoice_number || '',
    invoice_date: invoice.invoice_date || '', due_date: invoice.due_date || '',
    subtotal: invoice.subtotal ?? '', tax: invoice.tax ?? '', total: invoice.total ?? '',
    currency: invoice.currency || 'AUD', status: invoice.status || 'draft',
    dispute_reason: invoice.dispute_reason || '', notes: invoice.notes || '',
    expense_category: invoice.expense_category || '', expense_purpose: invoice.expense_purpose || ''
  } : {
    supplier_name: '', invoice_number: '', invoice_date: '', due_date: '',
    subtotal: '', tax: '', total: '', currency: 'AUD', status: 'draft',
    dispute_reason: '', notes: '', expense_category: '', expense_purpose: ''
  });
  const [lines, setLines] = useState([]);
  const [activity, setActivity] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [posting, setPosting] = useState(false);
  const [attachError, setAttachError] = useState('');
  const [dragover, setDragover] = useState(false);
  const fileRef = useRef(null);
  const [extracting, setExtracting] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [extractError, setExtractError] = useState('');
  const extractRef = useRef(null);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-extract on mount if file was dropped
  useEffect(() => {
    if (extractFile && !isEdit) extractInvoice(extractFile);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (!isEdit) return;
    Promise.all([
      aws.from('po_invoice_line_items').select('*').eq('invoice_id', invoice.id).order('created_at'),
      aws.from('invoice_activity_log').select('*').eq('invoice_id', invoice.id).order('created_at', { ascending: false }),
      aws.from('po_invoice_attachments').select('*').eq('invoice_id', invoice.id).order('uploaded_at', { ascending: false })
    ]).then(([lr, ar, atr]) => {
      setLines((lr.data || []).map(l => ({ id: l.id, catalog_id: l.catalog_id || null, sku: l.sku || '', description: l.description || '', qty: l.qty_invoiced ?? l.qty, unit_price: l.unit_price })));
      setActivity(ar.data || []);
      setAttachments(atr.data || []);
    });
  }, [invoice, isEdit]);

  const linesTotal = useMemo(() => lines.reduce((s, l) => s + (Number(l.qty || 0) * Number(l.unit_price || 0)), 0), [lines]);
  const hasLines = lines.length > 0;
  const effectiveSubtotal = hasLines ? linesTotal : Number(form.subtotal || 0);
  const effectiveTotal = hasLines ? effectiveSubtotal + Number(form.tax || 0) : Number(form.total || 0);


  async function extractInvoice(file) {
    setExtracting(true);
    setExtractError('');
    try {
      const base64 = await fileToBase64(file);
      // Some browsers report empty file.type for RTF; fall back to
      // the extension so the Lambda picks the right code path.
      let mime = file.type;
      if (!mime && /\.rtf$/i.test(file.name || '')) mime = 'application/rtf';
      const res = await fetch(import.meta.env.VITE_AWS_EXTRACT_INVOICE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_base64: base64, mime_type: mime, userPin: session.code })
      });
      if (!res.ok) {
        let msg = `Extract failed (HTTP ${res.status})`;
        try { const e = await res.json(); if (e?.error) msg = e.error; } catch {}
        setExtractError(`${file.name}: ${msg}`);
        setExtracting(false);
        return;
      }
      const d = await res.json();
      const filled = [];
      if (d.supplier_name) { setField('supplier_name', String(d.supplier_name)); filled.push('supplier_name'); }
      if (d.invoice_number) { setField('invoice_number', String(d.invoice_number)); filled.push('invoice_number'); }
      if (d.invoice_date) { setField('invoice_date', d.invoice_date); filled.push('invoice_date'); }
      if (d.due_date) { setField('due_date', d.due_date); filled.push('due_date'); }
      if (d.subtotal != null) { setField('subtotal', String(d.subtotal)); filled.push('subtotal'); }
      if (d.tax != null) { setField('tax', String(d.tax)); filled.push('tax'); }
      if (d.total != null) { setField('total', String(d.total)); filled.push('total'); }
      if (Array.isArray(d.line_items) && d.line_items.length > 0) {
        setLines(d.line_items.map(li => ({
          _local: Math.random().toString(36).slice(2, 8), sku: '',
          description: li.description || '', qty: li.qty != null ? String(li.qty) : '',
          unit_price: li.unit_cost != null ? String(li.unit_cost) : ''
        })));
        filled.push('line_items');
      }
      setPendingFile(file);
      // 200 with all-null fields means Claude saw nothing it recognised
      // as an invoice — surface that rather than silently looking broken.
      if (filled.length === 0) {
        setExtractError(`${file.name}: extracted but no invoice fields found. Fill manually.`);
      }
    } catch (err) {
      setExtractError(`${file.name}: ${err?.message || 'extract failed'}`);
    }
    setExtracting(false);
  }


  async function save() {
    if (!form.invoice_number) { alert('Invoice Number required.'); return; }
    if (!form.supplier_name) { alert('Contact required.'); return; }

    // Contact match check — if the typed name doesn't match an existing
    // contact, ask the user before silently creating one. Forces an
    // explicit "match existing or create new" decision instead of the
    // old behaviour where ensureContact() created a row in the dark.
    const typed = form.supplier_name.trim();
    // Match the typed value against EITHER company_name OR contact_person
    // so a person-only contact (e.g. "Andrew") satisfies the check without
    // forcing the user to create a duplicate.
    const { data: matches } = await aws.from('contacts')
      .select('id, company_name, contact_person')
      .or(`company_name.ilike.${typed},contact_person.ilike.${typed}`)
      .is('archived_at', null)
      .limit(1);
    if (!matches || matches.length === 0) {
      const proceed = window.confirm(
        `“${typed}” isn't in your contacts yet.\n\n` +
        `Click OK to save the invoice and add “${typed}” as a new contact.\n\n` +
        `Click Cancel to go back and pick an existing contact from the dropdown instead.`
      );
      if (!proceed) return;
    }

    setSaving(true);
    const data = {
      po_id: invoice?.po_id || null, supplier_id: null, supplier_name: form.supplier_name,
      po_number_text: invoice?.po_number_text || null, invoice_number: form.invoice_number,
      invoice_date: form.invoice_date || null, due_date: form.due_date || null,
      subtotal: String(effectiveSubtotal), tax: String(parseFloat(form.tax) || 0),
      total: String(effectiveTotal), amount_due: String(effectiveTotal),
      currency: form.currency, status: form.status,
      dispute_reason: form.dispute_reason || null, notes: form.notes || null
    };
    const lineData = lines.filter(l => l.description || l.sku || l.qty).map(l => ({
      po_line_item_id: null, catalog_id: l.catalog_id || null, sku: l.sku || null, description: l.description || null,
      qty_invoiced: Number(l.qty) || 0, unit_price: Number(l.unit_price) || 0
    }));
    const { data: result, error } = await aws.rpc('save_invoice', {
      p_invoice_id: invoice?.id || null, p_data: data, p_lines: lineData,
      p_actor_code: session.code, p_actor_name: session.name,
      p_expected_updated_at: invoice?.updated_at || null
    });
    if (error) { setSaving(false); alert(error.message?.includes('Conflict') ? 'Modified by another user.' : 'Save failed: ' + error.message); return; }
    const parsed = typeof result === 'string' ? JSON.parse(result) : result;
    const invoiceId = parsed?.id || invoice?.id;
    if (pendingFile && invoiceId) {
      const key = `gi/${invoiceId}/${Date.now()}-${pendingFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error: upErr, data: upData } = await storage.from(BUCKET).upload(key, pendingFile, { contentType: pendingFile.type, upsert: false });
      if (!upErr) {
        const storedPath = upData?.path || key;
        await authWrite(session, 'po_invoice_attachments', 'insert', {
          invoice_id: invoiceId, storage_path: storedPath, filename: pendingFile.name,
          mime_type: pendingFile.type, size_bytes: pendingFile.size, uploaded_by: session.code,
        });
        await logGiActivity(invoiceId, session, { action_type: 'attachment_added', after_value: pendingFile.name, notes: `proof:po-attachments:${storedPath}` });
      }
    }
    ensureContact(form.supplier_name);
    setSaving(false); onSaved?.(); onClose();
  }

  const addLine = () => setLines(ls => [...ls, { _local: Math.random().toString(36).slice(2, 8), catalog_id: null, sku: '', description: '', qty: '', unit_price: '' }]);
  const updateLine = (idx, patch) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const removeLine = (idx) => setLines(ls => ls.filter((_, i) => i !== idx));

  async function uploadFiles(files) {
    if (!invoice?.id) { setAttachError('Save first.'); return; }
    for (const file of files) {
      if (!isAcceptedInvoiceFile(file)) continue;
      const key = `gi/${invoice.id}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error: upErr, data: upData } = await storage.from(BUCKET).upload(key, file, { contentType: file.type, upsert: false });
      if (upErr) continue;
      const storedPath = upData?.path || key;
      await authWrite(session, 'po_invoice_attachments', 'insert', {
        invoice_id: invoice.id, storage_path: storedPath, filename: file.name,
        mime_type: file.type, size_bytes: file.size, uploaded_by: session.code,
      });
      await logGiActivity(invoice.id, session, { action_type: 'attachment_added', after_value: file.name, notes: `proof:po-attachments:${storedPath}` });
    }
    const { data } = await aws.from('po_invoice_attachments').select('*').eq('invoice_id', invoice.id).order('uploaded_at', { ascending: false });
    setAttachments(data || []);
    const { data: act } = await aws.from('invoice_activity_log').select('*').eq('invoice_id', invoice.id).order('created_at', { ascending: false });
    setActivity(act || []);
  }

  async function previewFile(storagePath, filename) {
    const { data, error } = await storage.from(BUCKET).createSignedUrl(storagePath, 3600);
    if (!error && data?.signedUrl) setPreview({ url: data.signedUrl, filename });
  }

  async function removeAttachment(att) {
    if (!confirm('Delete attachment?')) return;
    await authWrite(session, 'po_invoice_attachments', 'delete', null, { column: 'id', value: att.id });
    await storage.from(BUCKET).remove([att.storage_path]);
    await logGiActivity(invoice.id, session, { action_type: 'attachment_deleted', before_value: att.filename });
    setAttachments(a => a.filter(x => x.id !== att.id));
  }

  async function postComment() {
    if (!commentText.trim() || !invoice?.id) return;
    setPosting(true);
    await logGiActivity(invoice.id, session, { action_type: 'comment', notes: commentText.trim() });
    setCommentText('');
    setPosting(false);
    const { data } = await aws.from('invoice_activity_log').select('*').eq('invoice_id', invoice.id).order('created_at', { ascending: false });
    setActivity(data || []);
  }

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <aside className="drawer">
        <div className="drawer-header">
          <div>
            <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094', marginBottom: 4 }}>Invoice</div>
            <div className="drawer-title" style={{ marginBottom: 2 }}>{isEdit ? (invoice.invoice_number || 'Edit Invoice') : 'New Invoice'}</div>
            {isEdit && invoice.supplier_name && <div style={{ fontSize: 13, color: '#5C6166' }}>{invoice.supplier_name}</div>}
          </div>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>
        {isEdit && (() => {
          const total = Number(invoice.total || 0);
          const outstanding = getOutstanding(invoice, paidAmount);
          const ds = getDisplayStatus(invoice, paidAmount);
          const cur = invoice.currency || 'AUD';
          const isAud = cur === 'AUD';
          return (
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #EFEDE7', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <DrawerStat label="Total"
                value={formatMoney(total)}
                sub={isAud ? null : `${cur} ${total.toLocaleString()}`} />
              <DrawerStat label="Outstanding"
                value={outstanding > 0 ? formatMoney(outstanding) : '—'}
                sub={outstanding > 0 ? (isAud ? `Paid ${formatMoney(paidAmount)}` : `${cur} ${outstanding.toLocaleString()}`) : 'Fully paid'}
                accent={outstanding > 0} />
              <DrawerStat label="Due"
                value={invoice.due_date ? formatDate(invoice.due_date) : '—'} />
              <DrawerStat label="Status"
                value={<span className={`status-pill ${ds}`} style={{ fontSize: 11 }}>{ds.replace(/_/g, ' ')}</span>} />
            </div>
          );
        })()}
        {isEdit && (
          <div className="po-drawer-tabs">
            <button className={`po-drawer-tab ${tab === 'activity' ? 'active' : ''}`} onClick={() => setTab('activity')}>Activity</button>
            <button className={`po-drawer-tab ${tab === 'invoice' ? 'active' : ''}`} onClick={() => setTab('invoice')}>Invoice</button>
          </div>
        )}
        <div className="drawer-content">
          {(tab === 'invoice' || !isEdit) && (<>
            {!isEdit && (
              <>
                <div className={`po-extract-zone ${extracting ? 'extracting' : ''}`}
                  onClick={() => !extracting && extractRef.current?.click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer?.files?.[0]; if (f && isAcceptedInvoiceFile(f)) extractInvoice(f); }}>
                  {extracting ? 'Reading...' : pendingFile ? <span>Extracted from <strong>{pendingFile.name}</strong></span> : <span>Drop invoice to auto-fill <span className="muted-xs">PDF, PNG, JPG, RTF</span></span>}
                </div>
                <input ref={extractRef} type="file" accept="application/pdf,image/png,image/jpeg,application/rtf,text/rtf,.rtf" hidden onChange={e => { const f = e.target.files?.[0]; if (f) extractInvoice(f); e.target.value = ''; }} />
                {extractError && <div className="inline-error" style={{ marginTop: 6 }}>{extractError}</div>}
              </>
            )}

            <div className="form-group">
              <label className="form-label">Contact<span className="required-star">*</span></label>
              <ContactAutocomplete
                session={session}
                value={form.supplier_name}
                onChange={v => setField('supplier_name', v)}
                placeholder="Search or type contact name…"
              />
            </div>

            <div className="form-group"><label className="form-label">Invoice Number<span className="required-star">*</span></label><input className="form-input" value={form.invoice_number} onChange={e => setField('invoice_number', e.target.value)} placeholder="INV-001" /></div>
            <div className="row-2">
              <div className="form-group"><label className="form-label">Invoice Date</label><input type="date" className="form-input" value={form.invoice_date} onChange={e => setField('invoice_date', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Due Date</label><input type="date" className="form-input" value={form.due_date} onChange={e => setField('due_date', e.target.value)} /></div>
            </div>
            <div className="form-group"><label className="form-label">Currency</label>
              <select className="form-select" value={form.currency} onChange={e => setField('currency', e.target.value)}>
                <option value="AUD">AUD</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option>
              </select>
            </div>

            <div className="row-2">
              <div className="form-group">
                <label className="form-label">Expense Category</label>
                <select className="form-select" value={form.expense_category} onChange={e => setField('expense_category', e.target.value)}>
                  <option value="">Select...</option>
                  {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Purpose</label>
                <input className="form-input" value={form.expense_purpose} onChange={e => setField('expense_purpose', e.target.value)} placeholder="Brief accounting justification" />
              </div>
            </div>

            <div className="subsection">
              <div className="subsection-label">Line Items {lines.length > 0 && <span className="muted-xs">· {lines.length}</span>}</div>
              <div className="subsection-body">
                {lines.length > 0 && (
                  <div className="line-items-table">
                    <div className="po-line-head"><div>Description</div><div>Catalogue SKU</div><div style={{ textAlign: 'right' }}>Qty</div><div style={{ textAlign: 'right' }}>Unit $</div><div style={{ textAlign: 'right' }}>Total</div><div /></div>
                    {lines.map((line, idx) => {
                      const lt = Number(line.qty || 0) * Number(line.unit_price || 0);
                      return (
                        <div key={line.id || line._local} className="po-line-row">
                          <input type="text" value={line.description || ''} onChange={e => updateLine(idx, { description: e.target.value })} placeholder="Description" />
                          <div style={{ position: 'relative' }}>
                            <select value={line.catalog_id || ''} onChange={e => {
                              const catId = e.target.value || null;
                              const cat = catalog.find(c => c.id === catId);
                              const patch = { catalog_id: catId };
                              if (cat) {
                                if (!line.description) patch.description = cat.sku_name;
                                if (!line.sku) patch.sku = cat.sku_code || '';
                                if (line.unit_price === '' || line.unit_price == null) patch.unit_price = Number(cat.unit_cost) || 0;
                              }
                              updateLine(idx, patch);
                            }} style={{ fontSize: 11, borderColor: !line.catalog_id && line.description ? '#f59e0b' : undefined }}>
                              <option value="">{!line.catalog_id && line.description ? '⚠ unlinked' : '—'}</option>
                              {catalog.filter(c => c.status === 'active').map(c => (<option key={c.id} value={c.id}>{c.sku_code ? `${c.sku_code} · ` : ''}{c.sku_name}</option>))}
                            </select>
                          </div>
                          <input type="number" step="1" value={line.qty ?? ''} onChange={e => updateLine(idx, { qty: e.target.value })} style={{ textAlign: 'right' }} />
                          <input type="number" step="0.01" value={line.unit_price ?? ''} onChange={e => updateLine(idx, { unit_price: e.target.value })} style={{ textAlign: 'right' }} />
                          <div className="line-total-cell">{formatMoney(lt)}</div>
                          <button type="button" className="icon-del" onClick={() => removeLine(idx)}>×</button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={addLine}>+ Add line</button>
                {hasLines && <div className="muted-small" style={{ marginTop: 8 }}>Lines total: {formatMoney(linesTotal)}</div>}
              </div>
            </div>

            <div className="row-3">
              <div className="form-group"><label className="form-label">Subtotal</label>{hasLines ? <div className="readonly-value">{formatMoney(effectiveSubtotal)}</div> : <input type="number" className="form-input" step="0.01" value={form.subtotal} onChange={e => setField('subtotal', e.target.value)} placeholder="0.00" />}</div>
              <div className="form-group"><label className="form-label">Tax</label><input type="number" className="form-input" step="0.01" value={form.tax} onChange={e => setField('tax', e.target.value)} placeholder="0.00" /></div>
              <div className="form-group"><label className="form-label">Total</label>{hasLines ? <div className="readonly-value">{formatMoney(effectiveTotal)}</div> : <input type="number" className="form-input" step="0.01" value={form.total} onChange={e => setField('total', e.target.value)} placeholder="0.00" />}</div>
            </div>

            <div className="form-group">
              <div className={`attach-dropzone compact ${dragover ? 'dragover' : ''} ${!isEdit ? 'disabled' : ''}`}
                onClick={() => isEdit && fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); if (isEdit) setDragover(true); }}
                onDragLeave={() => setDragover(false)}
                onDrop={e => { e.preventDefault(); setDragover(false); if (isEdit) uploadFiles([...(e.dataTransfer?.files || [])]); }}>
                {isEdit ? <span>Drop files or click <span className="muted-xs">PDF, PNG, JPG, RTF</span></span> : 'Save first to attach'}
              </div>
              <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg,application/rtf,text/rtf,.rtf" multiple hidden onChange={e => { uploadFiles([...(e.target.files || [])]); e.target.value = ''; }} />
              {attachments.length > 0 && (
                <div className="inv-file-chips">
                  {attachments.map(a => (
                    <div key={a.id} className="inv-file-chip">
                      <span className="inv-file-chip-name">{a.filename}</span>
                      <span className="inv-file-chip-size">{formatBytes(a.size_bytes)}</span>
                      <button type="button" className="file-eye-btn" onClick={() => previewFile(a.storage_path, a.filename)} title="Preview">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.5"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/></svg>
                      </button>
                      <button type="button" className="icon-del" onClick={() => removeAttachment(a)}>×</button>
                    </div>
                  ))}
                </div>
              )}
              {attachError && <div className="inline-error">{attachError}</div>}
            </div>
          </>)}

          {tab === 'activity' && isEdit && (
            <div className="po-comments-tab">
              <div className="po-comment-input">
                <textarea className="form-textarea" placeholder="Add a comment..." value={commentText} onChange={e => setCommentText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) postComment(); }} />
                <button className="btn btn-primary btn-sm" disabled={posting || !commentText.trim()} onClick={postComment}>{posting ? 'Posting...' : 'Post'}</button>
              </div>
              <div className="po-unified-feed">
                {activity.length === 0 && <div className="po-card-activity-empty">No activity yet</div>}
                {activity.map(entry => {
                  const isComment = entry.action_type === 'comment';
                  const proofMatch = entry.notes?.match(/^proof:([^:]+):(.+)$/);
                  if (isComment) {
                    return (
                      <div key={entry.id} className="po-feed-comment">
                        <div className="po-feed-comment-header">
                          <span className="po-feed-comment-author">{entry.actor_name || 'Someone'}</span>
                          <span className="po-feed-comment-when">{formatWhen(entry.created_at)}</span>
                        </div>
                        <div className="po-feed-comment-body">{entry.notes}</div>
                      </div>
                    );
                  }
                  const label = entry.action_type === 'status_change'
                    ? `changed status: ${entry.before_value || '—'} → ${entry.after_value || '—'}`
                    : entry.action_type === 'payment_logged' ? `logged payment: ${entry.after_value || ''}`
                    : entry.action_type === 'attachment_added' ? `attached: ${entry.after_value || '(file)'}`
                    : entry.action_type === 'attachment_deleted' ? `removed: ${entry.before_value || '(file)'}`
                    : entry.action_type?.replace(/_/g, ' ');
                  return (
                    <div key={entry.id} className="po-feed-activity">
                      <span className="po-feed-activity-who">{entry.actor_name || 'System'}</span>
                      <span className="po-feed-sep">·</span>
                      <span className="po-feed-activity-what">
                        {label}
                        {proofMatch && (
                          <button type="button" className="file-eye-btn" style={{ marginLeft: 6 }} onClick={() => previewFile(proofMatch[2], entry.after_value || 'file')} title="View file">
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.5"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/></svg>
                          </button>
                        )}
                      </span>
                      <span className="po-feed-sep">·</span>
                      <span className="po-feed-activity-when">{formatWhen(entry.created_at)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {(tab === 'invoice' || !isEdit) && (
          <div className="drawer-footer">
            <button className="btn btn-secondary btn-full" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-full" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        )}
      </aside>
      {preview && <FilePreviewModal url={preview.url} filename={preview.filename} onClose={() => setPreview(null)} />}
    </>
  );
}
